import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isMetricsPanelSlug, metricsPanelPath, metricsPath, type MetricsPanelParent } from "../routes/slugs";

/**
 * Refleja en la URL el panel lateral abierto de una pestaña de Métricas (`/profile/metricas/<subtab>/<panel>`).
 *
 * El estado del panel (y su pila de "Volver") SIGUE viviendo en el componente: este hook solo lo espeja en la URL y,
 * al revés, abre el panel de un link directo. Así no se toca la lógica de los paneles.
 *  - Al montar con un panel en la URL, lo abre (`open`) y no pisa la URL.
 *  - Cuando cambia el panel visible (`current`), reemplaza la URL (sin apilar historial) conservando el query.
 *  - Si la URL cambia a otro panel con la pestaña ya montada (link interno), lo abre.
 */
export function useMetricsPanelUrl<T extends string>(parent: MetricsPanelParent, current: T | null, open: (panel: T) => void): void {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { section } = useParams<{ section?: string }>();
  const urlPanel = section && isMetricsPanelSlug(parent, section) ? (section as T) : null;
  const hydrated = useRef(false);

  // URL → panel (carga inicial o link interno a otro panel).
  useEffect(() => {
    if (urlPanel && urlPanel !== current) open(urlPanel);
    // Solo reacciona a la URL: `current` y `open` cambian con cada render del panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPanel]);

  // Panel → URL.
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      // Primera pasada con un panel en la URL: lo abre el efecto de arriba, no se pisa la URL todavía.
      if (urlPanel && !current) return;
    }
    // Si ya se salió de esta pestaña (ej. "Ir a Asignación" cierra el panel y cambia de tab), la URL no es de este hook.
    const base = metricsPath(parent);
    if (pathname !== base && !pathname.startsWith(`${base}/`)) return;
    const target = metricsPanelPath(parent, current);
    if (pathname !== target) navigate(`${target}${search}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);
}
